import React, { useEffect, useState } from 'react';
import { Shell } from '../../components/layout/Shell';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Certificate, FolderSimple } from '@phosphor-icons/react';
import FileUploader from '../../components/hikari/FileUploader';
import ProjectMedia from '../../components/hikari/ProjectMedia';
import ImageDropzone from '../../components/hikari/ImageDropzone';

const CandidateProfile = () => {
  const [profile, setProfile] = useState(null);
  const [families, setFamilies] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newCred, setNewCred] = useState({ title: '', issuer: '', year: '' });
  const [newProject, setNewProject] = useState({ title: '', description: '', url: '', thumbnail_url: '', skills: '' });

  useEffect(() => {
    api.get('/candidates/me').then((r) => setProfile(r.data));
    api.get('/role-families').then((r) => setFamilies(r.data || []));
  }, []);

  const acceptSuggestion = async (familyId) => {
    try {
      await api.post('/candidates/me/accept-suggestion', { family_id: familyId });
      setProfile({
        ...profile,
        role_family_ids: Array.from(new Set([...(profile.role_family_ids || []), familyId])),
      });
      toast.success('Added to your target families');
    } catch {
      toast.error('Could not add');
    }
  };

  const dismissSuggestion = async (familyId) => {
    try {
      await api.post('/candidates/me/dismiss-suggestion', { family_id: familyId });
      setProfile({
        ...profile,
        dismissed_family_suggestions: [...(profile.dismissed_family_suggestions || []), familyId],
      });
    } catch { /* silent */ }
  };

  const updateField = (field, value) => setProfile({ ...profile, [field]: value });

  const toggleFamily = (id) => {
    const current = new Set(profile.role_family_ids || []);
    if (current.has(id)) current.delete(id); else current.add(id);
    updateField('role_family_ids', Array.from(current));
  };

  const saveCore = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/candidates/me', {
        full_name: profile.full_name,
        headline: profile.headline,
        location: profile.location,
        country: profile.country,
        work_authorization: profile.work_authorization,
        availability: profile.availability,
        role_family_ids: profile.role_family_ids || [],
        experience_tier: profile.experience_tier || null,
      });
      setProfile(data);
      toast.success('Profile saved');
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addCredential = async () => {
    if (!newCred.title) return toast.error('Credential title required');
    const { data } = await api.post('/candidates/me/credentials', {
      title: newCred.title,
      issuer: newCred.issuer,
      year: newCred.year ? Number(newCred.year) : null,
    });
    setProfile(data);
    setNewCred({ title: '', issuer: '', year: '' });
    toast.success('Credential added');
  };

  const addProject = async () => {
    if (!newProject.title) return toast.error('Project title required');
    const { data } = await api.post('/candidates/me/projects', {
      title: newProject.title,
      description: newProject.description,
      url: newProject.url || null,
      thumbnail_url: newProject.thumbnail_url || null,
      skills: newProject.skills.split(',').map((s) => s.trim()).filter(Boolean),
    });
    setProfile(data);
    setNewProject({ title: '', description: '', url: '', thumbnail_url: '', skills: '' });
    toast.success('Project added');
  };

  if (!profile) return <Shell><div className="p-8 mono text-sm">Loading...</div></Shell>;

  return (
    <Shell>
      <div className="max-w-[1200px] mx-auto px-6 py-10">
        <div className="label-eyebrow text-primary mb-2">YOUR PROFILE</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mb-8">Show what you can do.</h1>

        <div className="border border-border bg-white p-8 mb-6 space-y-5" data-testid="profile-core">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="label-eyebrow text-muted-foreground">Full name</Label>
              <Input value={profile.full_name || ''} onChange={(e) => updateField('full_name', e.target.value)} className="mt-2 rounded-none h-11" data-testid="profile-name" />
            </div>
            <div>
              <Label className="label-eyebrow text-muted-foreground">Headline</Label>
              <Input value={profile.headline || ''} onChange={(e) => updateField('headline', e.target.value)} placeholder="e.g. Ops Coordinator with 5 yrs in logistics" className="mt-2 rounded-none h-11" data-testid="profile-headline" />
              <p className="mono text-[11px] text-muted-foreground mt-2 leading-relaxed" data-testid="profile-headline-hint">
                More examples: <span className="text-foreground">"Self-taught operations coordinator"</span> · <span className="text-foreground">"Ran a 4-truck moving business · 1,200+ jobs"</span> · <span className="text-foreground">"2nd-year design student · motion + brand"</span> · <span className="text-foreground">"Returning to work after 5 years caregiving"</span>
              </p>
            </div>
            <div>
              <Label className="label-eyebrow text-muted-foreground">Location</Label>
              <Input value={profile.location || ''} onChange={(e) => updateField('location', e.target.value)} className="mt-2 rounded-none h-11" data-testid="profile-location" />
            </div>
            <div>
              <Label className="label-eyebrow text-muted-foreground">Country</Label>
              <Select value={profile.country} onValueChange={(v) => updateField('country', v)}>
                <SelectTrigger className="mt-2 rounded-none h-11" data-testid="profile-country"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Canada">Canada</SelectItem>
                  <SelectItem value="United States">United States</SelectItem>
                  <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                  <SelectItem value="India">India</SelectItem>
                  <SelectItem value="Global">Global / Remote</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow text-muted-foreground">Work authorization</Label>
              <Select value={profile.work_authorization} onValueChange={(v) => updateField('work_authorization', v)}>
                <SelectTrigger className="mt-2 rounded-none h-11" data-testid="profile-workauth"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="citizen">Citizen</SelectItem>
                  <SelectItem value="permanent_resident">Permanent resident</SelectItem>
                  <SelectItem value="work_permit">Work permit holder</SelectItem>
                  <SelectItem value="student">Student visa</SelectItem>
                  <SelectItem value="requires_sponsorship">Requires sponsorship</SelectItem>
                  <SelectItem value="unknown">Prefer not to say</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow text-muted-foreground">Availability</Label>
              <Select value={profile.availability} onValueChange={(v) => updateField('availability', v)}>
                <SelectTrigger className="mt-2 rounded-none h-11" data-testid="profile-availability"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Actively looking</SelectItem>
                  <SelectItem value="passive">Open to opportunities</SelectItem>
                  <SelectItem value="not_looking">Not looking</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Career stage — controls scoring weights for fairness across life stages */}
          <div className="border-t border-border pt-5" data-testid="career-stage-section">
            <Label className="label-eyebrow text-muted-foreground">Career stage</Label>
            <p className="text-xs text-muted-foreground mt-1 mb-3 leading-relaxed">
              Pick what fits today. <span className="text-foreground font-semibold">Early career</span> swaps in
              softer scoring weights so a lack of work history isn't a structural ceiling — your assessment
              scores and projects carry more of the fit number.
            </p>
            <div className="grid sm:grid-cols-2 gap-2" data-testid="career-stage-picker">
              {[
                { v: 'early_career', label: 'Early career', sub: 'Student, first job, no work history yet' },
                { v: 'returner', label: 'Returner', sub: 'Coming back after caregiving, business or break' },
                { v: 'mid', label: 'Mid-career', sub: '2-7 yrs in your field' },
                { v: 'experienced', label: 'Experienced', sub: '8+ yrs in your field' },
              ].map((t) => {
                const on = profile.experience_tier === t.v;
                return (
                  <button
                    key={t.v}
                    type="button"
                    onClick={() => updateField('experience_tier', t.v)}
                    className={`text-left p-3 border ${
                      on
                        ? 'bg-foreground text-background border-foreground'
                        : 'bg-white text-foreground border-border hover:border-foreground'
                    }`}
                    data-testid={`career-stage-${t.v}`}
                  >
                    <div className="label-eyebrow flex items-center gap-1">
                      {on && <span className="mono">✓</span>} {t.label}
                    </div>
                    <div className={`mono text-[11px] mt-1 ${on ? 'opacity-80' : 'text-muted-foreground'}`}>
                      {t.sub}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Role family interests — controls which assessments show up */}
          <div className="border-t border-border pt-5" data-testid="role-family-picker">
            <Label className="label-eyebrow text-muted-foreground">Target role families</Label>
            <p className="text-xs text-muted-foreground mt-1 mb-3">
              Pick the roles you'd actually take. Assessments and relevant jobs will default to these.
            </p>
            <div className="flex flex-wrap gap-2">
              {families.map((f) => {
                const picked = (profile.role_family_ids || []).includes(f.id);
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => toggleFamily(f.id)}
                    className={`label-eyebrow px-3 py-2 border transition-colors ${picked ? 'bg-foreground text-background border-foreground' : 'bg-transparent text-foreground border-border hover:border-foreground'}`}
                    data-testid={`family-chip-${f.slug || f.id}`}
                  >
                    {picked ? '✓ ' : '+ '}{f.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Auto-suggested families based on prerequisite_signals match */}
          {(() => {
            const dismissed = new Set(profile.dismissed_family_suggestions || []);
            const picked = new Set(profile.role_family_ids || []);
            const suggestions = (profile.family_match_details || [])
              .filter((s) => !dismissed.has(s.family_id) && !picked.has(s.family_id))
              .slice(0, 4);
            if (suggestions.length === 0) return null;
            return (
              <div className="border-t border-border pt-5" data-testid="suggested-families">
                <Label className="label-eyebrow text-primary">SUGGESTED FOR YOU — based on your profile</Label>
                <p className="text-xs text-muted-foreground mt-1 mb-3">
                  We spotted keywords in your background that match these role families. Add the ones that fit.
                </p>
                <div className="space-y-2">
                  {suggestions.map((s) => (
                    <div key={s.family_id} className="flex items-center justify-between border border-primary/30 bg-primary/5 p-3" data-testid={`suggestion-${s.family_slug}`}>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm">{s.name}</div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(s.matched_signals || []).slice(0, 5).map((m) => (
                            <span key={m} className="label-eyebrow bg-primary/10 text-primary px-2 py-0.5">{m}</span>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => dismissSuggestion(s.family_id)} data-testid={`dismiss-${s.family_slug}`}>Dismiss</Button>
                        <Button size="sm" className="rounded-none h-8" onClick={() => acceptSuggestion(s.family_id)} data-testid={`accept-${s.family_slug}`}>+ Add</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          <Button onClick={saveCore} disabled={saving} className="rounded-none" data-testid="save-profile">
            {saving ? 'Saving...' : 'Save profile'}
          </Button>
        </div>

        {/* Credentials */}
        <div className="border border-border bg-white p-8 mb-6" data-testid="credentials-section">
          <div className="flex items-center gap-2 mb-4">
            <Certificate size={20} weight="duotone" className="text-primary" />
            <div className="label-eyebrow">CREDENTIALS</div>
          </div>
          <div className="space-y-2 mb-5">
            {profile.credentials.length === 0 && <div className="text-sm text-muted-foreground">No credentials yet.</div>}
            {profile.credentials.map((c) => (
              <div key={c.id} className="flex items-center justify-between border border-border p-3 text-sm">
                <div>
                  <span className="font-medium">{c.title}</span>
                  {c.issuer && <span className="text-muted-foreground"> · {c.issuer}</span>}
                  {c.year && <span className="mono text-muted-foreground"> · {c.year}</span>}
                </div>
                {c.verified && <span className="label-eyebrow text-primary">VERIFIED</span>}
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-4 gap-2">
            <Input placeholder="Credential title" value={newCred.title} onChange={(e) => setNewCred({ ...newCred, title: e.target.value })} className="rounded-none h-11" data-testid="cred-title" />
            <Input placeholder="Issuer" value={newCred.issuer} onChange={(e) => setNewCred({ ...newCred, issuer: e.target.value })} className="rounded-none h-11" data-testid="cred-issuer" />
            <Input placeholder="Year" type="number" value={newCred.year} onChange={(e) => setNewCred({ ...newCred, year: e.target.value })} className="rounded-none h-11" data-testid="cred-year" />
            <Button onClick={addCredential} className="rounded-none h-11" data-testid="cred-add"><Plus size={16} className="mr-1" /> Add</Button>
          </div>
        </div>

        {/* Projects */}
        <div className="border border-border bg-white p-8" data-testid="projects-section">
          <div className="flex items-center gap-2 mb-2">
            <FolderSimple size={20} weight="duotone" className="text-primary" />
            <div className="label-eyebrow">PROJECTS / WORK SAMPLES</div>
          </div>
          <p className="mono text-[11px] text-muted-foreground mb-5 leading-relaxed" data-testid="projects-hint">
            Anything you've actually done counts: a freelance gig, a side build, a small business you ran, a class project, a volunteer dashboard, a portfolio piece. <span className="text-foreground">If you did it, it's proof.</span>
          </p>
          <div className="grid sm:grid-cols-2 gap-3 mb-5">
            {profile.projects.length === 0 && (
              <div className="text-sm text-muted-foreground sm:col-span-2">No projects yet.</div>
            )}
            {profile.projects.map((p) => (
              <div key={p.id} className="border border-border bg-white overflow-hidden" data-testid={`proj-card-${p.id}`}>
                <ProjectMedia project={p} aspect="16/9" />
                <div className="p-3">
                  <div className="font-medium text-sm">{p.title}</div>
                  {p.description && (
                    <div className="text-muted-foreground text-xs mt-1 line-clamp-3">{p.description}</div>
                  )}
                  {p.url && (
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noreferrer"
                      className="label-eyebrow text-primary hover:underline mt-2 inline-block"
                    >
                      OPEN ↗
                    </a>
                  )}
                  {p.skills?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {p.skills.map((s) => (
                        <span key={s} className="label-eyebrow bg-secondary px-2 py-0.5">{s}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            <Input placeholder='e.g. "Acme Moving Co — 4 trucks, 4 yrs"' value={newProject.title} onChange={(e) => setNewProject({ ...newProject, title: e.target.value })} className="rounded-none h-11" data-testid="proj-title" />
            <Input placeholder="URL (portfolio, GitHub, Behance, YouTube — optional)" value={newProject.url} onChange={(e) => setNewProject({ ...newProject, url: e.target.value })} className="rounded-none h-11" data-testid="proj-url" />
            <div className="md:col-span-2">
              <ImageDropzone
                value={newProject.thumbnail_url}
                onChange={(url) => setNewProject({ ...newProject, thumbnail_url: url })}
                testIdPrefix="proj-thumbnail"
              />
            </div>
            <Textarea placeholder='Brief description — e.g. "Ran daily ops + 6-person crew, handled 1,200+ jobs, 4.8 Google rating, managed vendor relationships and dispatch."' value={newProject.description} onChange={(e) => setNewProject({ ...newProject, description: e.target.value })} className="rounded-none md:col-span-2" data-testid="proj-desc" />
            <Input placeholder="Skills (comma separated: dispatch, vendor_management, customer_service)" value={newProject.skills} onChange={(e) => setNewProject({ ...newProject, skills: e.target.value })} className="rounded-none h-11 md:col-span-2" data-testid="proj-skills" />
            <Button onClick={addProject} className="rounded-none h-11 md:col-span-2" data-testid="proj-add"><Plus size={16} className="mr-1" /> Add project</Button>
          </div>
        </div>

        {/* File uploads */}
        <div className="grid md:grid-cols-2 gap-0 grid-skeleton mt-6">
          <div className="p-6 bg-white">
            <FileUploader purpose="resume" label="Résumé" accept=".pdf,.doc,.docx" />
          </div>
          <div className="p-6 bg-white">
            <FileUploader purpose="work_sample" label="Work samples" />
          </div>
        </div>
      </div>
    </Shell>
  );
};

export default CandidateProfile;
